import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  KitFlexRow,
  KitIcon,
  KitLink,
  KitLocalePicker,
  KitUtilCommon,
  KitUtilLocale,
  ThemeColors as colors,
  ThemeConstants,
} from '@chargepoint/cp-toolkit';
import localStorage from '../localStorage';
import utils from '../common/utils';

const { breakpoints, fontSize, spacing } = ThemeConstants;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  @media all and (max-width: ${breakpoints.md}px) {
    flex-direction: column;
    justify-content: center;
  }
  align-items: center;
  background-color: ${colors.gray_10};
  padding: ${spacing.m}rem;
  & * {
    font-size: ${fontSize.text_14}rem;
  }
`;

const Copyright = styled.div`
  order: 0;
  @media all and (max-width: ${breakpoints.md}px) {
    order: 1;
  }
`;

const Links = styled(KitFlexRow)`
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  order: 1;
  @media all and (max-width: ${breakpoints.md}px) {
    order: 0;
  }
  & > * {
    margin-left: ${spacing.m}rem;
  }
  & > *:first-child {
    margin-left: 0;
  }
`;

const LocalePickerContainer = styled(KitFlexRow)`
  align-items: center;
`;

const LanguageIcon = styled(KitIcon)`
  margin-right: ${spacing.xs}rem;
`;

function Footer() {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const [showLocalePicker, setShowLocalePicker] = useState(false);
  const [localePickerResult, setLocalePickerResult] = useState(null);
  const [supportedLocales, setSupportedLocales] = useState(undefined);

  useEffect(() => {
    const subdomain = utils.getSubdomain();
    const regionCode = KitUtilLocale.getRegionCodeForSSOSubdomain(t, subdomain);
    const defaultLocaleCode = KitUtilLocale.getDefaultLocaleCodeForRegionCode(t, regionCode);
    const localeCode = localStorage.getLocaleCode(defaultLocaleCode);
    setLocalePickerResult(KitLocalePicker.getKitLocalePickerResult(t, regionCode, localeCode));
  }, [t, setLocalePickerResult]);

  const footerCopyright = t('footer_copyright', {
    year: new Date().getFullYear(),
    company_name: window.cp_constants.STRING_COMPANY_NAME,
  });

  const onLocaleChanged = (result) => {
    setShowLocalePicker(false);

    localStorage.setLocaleCode(result.localeCode);

    const supportedLocale = KitUtilLocale.getSupportedLocale(result.nosCookieLocale);

    i18n.changeLanguage(supportedLocale);

    const currentSubdomain = utils.getSubdomain();
    if (currentSubdomain !== result.ssoSubdomain) {
      // Switch subdomain aka NOSLETs
      const queryParamsObject = KitUtilCommon.getQueryObject(window.location.search);
      queryParamsObject.locale = result.localeCode; // Forcing locale to be loaded on new subdomain via query param!!!
      // IMPORTANT: If there's a username query param it should be deleted before redirecting to another NOSLET otherwise
      // backend could force a redirect back to the users proper region NOSLET. Ping pong redirection is bad.
      delete queryParamsObject.username;
      const redirectTo = `${window.location.protocol}//${
        result.ssoSubdomain
      }.${utils.getDomain()}/${KitUtilCommon.getQueryString(queryParamsObject)}`;
      window.location = redirectTo;
    } else {
      // IMPORTANT:
      // Need to remove the "locale" query param and refresh page.
      // Otherwise, that "locale" query param will take priority over users selected region/locale.
      let path = window.location.pathname;
      const redirectQueryParam = KitUtilCommon.getQueryParam(window.location.search, 'redirect');
      if (redirectQueryParam) {
        path += KitUtilCommon.getQueryString({
          redirect: redirectQueryParam,
        });
      }
      history.push(path);
    }
  };

  let corpAboutUrl;
  let corpContactUrl;
  let privacyPolicyUrl;
  if (localePickerResult) {
    corpAboutUrl = `https://${localePickerResult.corporateUrl}/about`;
    corpContactUrl = `https://${localePickerResult.corporateUrl}/support`;
    privacyPolicyUrl = utils.makePrivacyPolicyUrl(localePickerResult);
  }

  let californiaPrivacyNoticeLink;
  if (localePickerResult && localePickerResult.displayCaliforniaPrivacy) {
    const policyUrl = utils.makeCaliforniaPrivacyUrl(localePickerResult);
    californiaPrivacyNoticeLink = (
      <a href={policyUrl} target='_blank' rel='noopener noreferrer'>
        {t('california_privacy_notice')}
      </a>
    );
  }

  const currentLocalizedLanguageName = localePickerResult?.localizedLanguageName || 'English';
  const localePickerAriaLabel = t('footer_language_picker_link_aria_label', {
    language: currentLocalizedLanguageName,
  });

  const getSupportedLocales = () => {
    return fetch(
      `https://account.${window.cp_constants.ENV_DOMAIN}/account/v1/metadata/languages`,
      {
        method: 'GET',
      }
    )
      .then((response) => {
        return response.json();
      })
      .then((languages) => {
        return languages.filter((l) => l.locale !== '').map((l) => l.locale);
      });
  };

  const openLocalePicker = async () => {
    try {
      setSupportedLocales(await getSupportedLocales());
      setShowLocalePicker(true);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Container>
      <Copyright>{footerCopyright}</Copyright>
      <Links>
        <KitLink onClick={openLocalePicker} aria-label={localePickerAriaLabel}>
          <LocalePickerContainer>
            <LanguageIcon
              icon='web'
              size={`${ThemeConstants.iconSize.m}rem`}
              fill={colors.blue_55}
            />
            {currentLocalizedLanguageName}
          </LocalePickerContainer>
        </KitLink>
        <a href={corpAboutUrl} target='_blank' rel='noopener noreferrer'>
          {t('about_us')}
        </a>
        <a href={corpContactUrl} target='_blank' rel='noopener noreferrer'>
          {t('contact_us')}
        </a>
        <a href={privacyPolicyUrl} target='_blank' rel='noopener noreferrer'>
          {t('privacy_policy')}
        </a>
        {californiaPrivacyNoticeLink}
      </Links>
      <KitLocalePicker
        selectedLocale='en-US'
        show={showLocalePicker}
        onHide={() => setShowLocalePicker(false)}
        onSelect={onLocaleChanged}
        t={t}
        supportedLocales={supportedLocales}
      />
    </Container>
  );
}

export default Footer;
